import { motion } from "framer-motion";
import { useLocale } from "../i18n/LocaleContext";
import { BrandLogo } from "./BrandLogo";

export function HeroSection() {
  const { copy, locale } = useLocale();
  const { hero } = copy;
  const isChinese = locale === "zh" || locale === "vi";
  const titleClass =
    isChinese
      ? "max-w-[10.4em] text-[1.74rem] leading-[1.2] tracking-[-0.02em] sm:text-[2.52rem] sm:leading-[1.1] xl:text-[3.08rem]"
      : "max-w-[10.1ch] text-[1.86rem] leading-[1] tracking-[-0.04em] sm:text-[2.86rem] xl:text-[3.28rem]";
  const titleFontClass = isChinese ? "font-sans font-medium" : "font-display font-semibold";

  return (
    <section
      id="home"
      className="relative overflow-hidden px-5 pb-20 pt-24 sm:px-8 sm:pb-24 sm:pt-28 lg:px-12 lg:pb-32 lg:pt-40"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(191,225,234,0.3),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(173,196,206,0.18),transparent_32%)]" />
      <div className="absolute left-[-8%] top-16 h-56 w-56 rounded-full bg-[#c9e5ee]/45 blur-3xl" />
      <div className="absolute right-[-4%] top-32 h-48 w-48 rounded-full bg-white/40 blur-3xl" />
      <div className="absolute inset-0 bg-grid bg-[size:120px_120px] opacity-[0.045]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-[0.72fr_1.28fr] lg:gap-28">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
        >
          <div className="mb-9 inline-flex items-center gap-4 rounded-full border border-white/42 bg-[linear-gradient(180deg,rgba(255,255,255,0.8),rgba(245,250,252,0.68))] px-4 py-2 shadow-[0_8px_18px_rgba(103,131,145,0.04)] backdrop-blur">
            <BrandLogo size="md" className="shrink-0" />
            <div className="text-left">
              <p className="text-[10px] uppercase tracking-[0.34em] text-aurora/80">
                {hero.badge}
              </p>
              <p className="mt-1 text-[12px] tracking-[0.14em] text-[#4c6067]">
                {hero.companyLine}
              </p>
            </div>
          </div>
          <p className="mb-6 text-[0.66rem] uppercase tracking-[0.56em] text-aurora/64 sm:text-[0.72rem]">
            {hero.eyebrow}
          </p>
          <h1 className={`${titleClass} ${titleFontClass} text-[#1f2b31]`}>
            {hero.title}
          </h1>
          <p className="mt-7 max-w-[18rem] text-[0.88rem] leading-[1.95] text-soft/54 sm:text-[0.94rem]">
            {hero.intro}
          </p>
          <div className="mt-11 flex flex-wrap items-center gap-3">
            <motion.a
              whileHover={{ y: -2, boxShadow: "0 16px 34px rgba(129,168,181,0.22)" }}
              whileTap={{ scale: 0.98 }}
              href="#brands"
              className="inline-flex rounded-full bg-[linear-gradient(135deg,#89adbb,#9dbfc9)] px-6 py-3.5 text-[9px] font-medium uppercase tracking-[0.34em] text-white shadow-[0_16px_34px_rgba(129,168,181,0.18)] sm:px-7 sm:py-4"
            >
              {hero.primaryCta}
            </motion.a>
            <motion.a
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              href="#about"
              className="inline-flex rounded-full border border-white/45 bg-[rgba(255,255,255,0.66)] px-6 py-3.5 text-[9px] uppercase tracking-[0.34em] text-[#1f2b31] shadow-[0_12px_24px_rgba(103,131,145,0.05)] backdrop-blur sm:px-7 sm:py-4"
            >
              {hero.secondaryCta}
            </motion.a>
          </div>
          <div className="mt-14 grid max-w-[25rem] gap-0 border-t border-[rgba(143,171,181,0.18)] pt-5 sm:grid-cols-3">
            {hero.credibilityMetrics.slice(0, 3).map((metric) => (
              <div
                key={metric.label}
                className="border-b border-[rgba(143,171,181,0.16)] py-3 text-soft/58 sm:border-b-0 sm:border-r sm:pr-4 last:border-r-0"
              >
                <div className="font-display text-[0.96rem] text-[#1f2b31] sm:text-[1.04rem]">{metric.value}</div>
                <div className="mt-1 text-[8px] uppercase tracking-[0.28em] text-soft/46">
                  {metric.label}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 36 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="relative lg:pl-10"
        >
          <div className="relative mx-auto aspect-[0.98/1.02] w-full max-w-[660px] overflow-hidden rounded-[2.9rem] border border-white/48 bg-[linear-gradient(180deg,rgba(255,255,255,0.88),rgba(242,248,251,0.88))] p-3.5 sm:aspect-[1.1/0.98] sm:rounded-[3.25rem] sm:p-5 shadow-[0_20px_44px_rgba(103,131,145,0.08)] backdrop-blur">
            <div className="absolute inset-5 overflow-hidden rounded-[2.35rem] shadow-[inset_0_0_0_1px_rgba(217,230,236,0.88)]">
              <img
                src="/official-brands/roundlab.jpg"
                alt="QIANDU K-Beauty portfolio"
                className="h-full w-full object-contain bg-[rgba(246,248,249,0.92)] p-6 sm:p-7"
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(31,43,49,0.08)_56%,rgba(31,43,49,0.3))]" />
            </div>

            <div className="absolute left-5 top-5 rounded-full border border-white/45 bg-[rgba(255,255,255,0.56)] px-4 py-2 text-[9px] uppercase tracking-[0.28em] text-soft/54 shadow-[0_10px_22px_rgba(103,131,145,0.05)] backdrop-blur sm:left-9 sm:top-9">
              {hero.badge}
            </div>

            <div className="absolute right-5 top-5 rounded-full border border-white/35 bg-[rgba(76,99,111,0.15)] px-4 py-2 text-[8px] uppercase tracking-[0.32em] text-white/84 backdrop-blur sm:right-9 sm:top-9">
              {hero.sideTag}
            </div>

            <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 sm:right-8">
              <div className="max-w-[12.5rem] rounded-[2rem] border border-white/44 bg-[linear-gradient(180deg,rgba(255,255,255,0.78),rgba(244,249,251,0.7))] p-5 shadow-[0_14px_28px_rgba(103,131,145,0.05)] backdrop-blur">
                <div className="text-[7px] uppercase tracking-[0.36em] text-soft/44">
                  {hero.visualEyebrow ?? "Signature Portfolio"}
                </div>
                <div className="mt-3 font-display text-[0.94rem] leading-tight text-[#1f2b31] sm:text-[1.18rem]">
                  {hero.visualTitle ?? "Korean beauty, presented with a cleaner global point of view."}
                </div>
                <div className="mt-4 inline-flex rounded-full bg-[rgba(255,255,255,0.52)] px-3 py-1 text-[7px] uppercase tracking-[0.34em] text-soft/50">
                  {hero.microTag}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
